import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import { CapturaFoto, hayCamara } from './camara.js';
import {
  IconoArchivo,
  IconoCamara,
  IconoEstadoAbierta,
  IconoEstadoCompleta,
  IconoEstadoEnCurso,
  IconoEstadoEnEspera,
  IconoEstadoVencida,
  IconoGaleria,
} from './iconos.js';
import {
  estadoVisualOt,
  ETIQUETA_ESTADO_ACTIVO,
  ETIQUETA_ESTADO_SOLICITUD,
  ETIQUETA_ESTADO_VISUAL_OT,
  ETIQUETA_PRIORIDAD,
  type EntidadAdjunto,
  type EstadoActivo,
  type EstadoOT,
  type EstadoSolicitud,
  type EstadoVisualOT,
  type Prioridad,
} from '@cmms/shared';
import { api, ErrorApi } from '../servicios/api.js';
import { useEsMovil } from '../servicios/responsive.js';
import { useSesion } from '../servicios/sesion.js';
import { permisoDeRuta } from '../servicios/rutas.js';
import { IconoCheck } from './iconos.js';

export type Tono = 'gris' | 'azul' | 'verde' | 'ambar' | 'roja';

export function Etiqueta({ tono, children }: { tono: Tono; children: ReactNode }): JSX.Element {
  return <span className={`etiqueta ${tono}`}>{children}</span>;
}

export const TONO_ESTADO_VISUAL_OT: Record<EstadoVisualOT, Tono> = {
  ABIERTA: 'azul',
  EN_ESPERA: 'ambar',
  EN_CURSO: 'azul',
  COMPLETA: 'verde',
  VENCIDA: 'roja',
};

/** Un ícono por estado en vez del punto de color genérico: se reconocen de un
 * vistazo en la etiqueta, el calendario y los botones de cambio de estado. */
export const ICONO_ESTADO_VISUAL_OT: Record<EstadoVisualOT, () => JSX.Element> = {
  ABIERTA: IconoEstadoAbierta,
  EN_ESPERA: IconoEstadoEnEspera,
  EN_CURSO: IconoEstadoEnCurso,
  COMPLETA: IconoEstadoCompleta,
  VENCIDA: IconoEstadoVencida,
};

/**
 * `vencida` (calculada por el backend, §14.1) pesa más que el estado real:
 * una OT abierta o en curso pasada de fecha se muestra como "Vencida".
 */
export function EstadoOtEtiqueta({ estado, vencida }: { estado: EstadoOT; vencida?: boolean }): JSX.Element {
  const visual = estadoVisualOt(estado, vencida ?? false);
  const Icono = ICONO_ESTADO_VISUAL_OT[visual];
  return (
    <Etiqueta tono={TONO_ESTADO_VISUAL_OT[visual]}>
      <Icono />
      {ETIQUETA_ESTADO_VISUAL_OT[visual]}
    </Etiqueta>
  );
}

const TONO_PRIORIDAD: Record<Prioridad, Tono> = {
  ALTA: 'roja',
  MEDIA: 'ambar',
  BAJA: 'azul',
  NINGUNA: 'gris',
};

export function PrioridadEtiqueta({ prioridad }: { prioridad: Prioridad }): JSX.Element {
  return <Etiqueta tono={TONO_PRIORIDAD[prioridad]}>{ETIQUETA_PRIORIDAD[prioridad]}</Etiqueta>;
}

export const TONO_ESTADO_SOLICITUD: Record<EstadoSolicitud, Tono> = {
  PENDIENTE: 'ambar',
  APROBADA: 'verde',
  DECLINADA: 'roja',
};

export function EstadoSolicitudEtiqueta({ estado }: { estado: EstadoSolicitud }): JSX.Element {
  return <Etiqueta tono={TONO_ESTADO_SOLICITUD[estado]}>{ETIQUETA_ESTADO_SOLICITUD[estado]}</Etiqueta>;
}

export const TONO_ESTADO_ACTIVO: Record<EstadoActivo, Tono> = {
  EN_LINEA: 'verde',
  FUERA_DE_LINEA: 'roja',
  NO_REALIZAR_SEGUIMIENTO: 'gris',
};

export function EstadoActivoEtiqueta({ estado }: { estado: EstadoActivo }): JSX.Element {
  return (
    <Etiqueta tono={TONO_ESTADO_ACTIVO[estado]}>
      <span className="punto" />
      {ETIQUETA_ESTADO_ACTIVO[estado]}
    </Etiqueta>
  );
}

export function Indicador({
  titulo,
  valor,
  nota,
  to,
  onClick,
  tono,
  activo,
  className,
}: {
  titulo: string;
  valor: ReactNode;
  nota?: ReactNode;
  to?: string;
  /** Filtro rápido en la misma pantalla (sin navegar), como alternativa a `to`. */
  onClick?: () => void;
  /** Acento de color (el mismo lenguaje que las etiquetas de estado) para que el KPI se identifique de un vistazo. */
  tono?: Tono;
  /** Resalta el KPI cuyo filtro está aplicado ahora mismo. */
  activo?: boolean;
  className?: string;
}): JSX.Element {
  const { puede } = useSesion();
  // Un KPI solo es un enlace si el usuario puede abrir su destino; si no, se muestra como tarjeta.
  const permiso = to ? permisoDeRuta(to) : null;
  const destinoAccesible = !to || !permiso || puede(permiso);
  const enlace = to && destinoAccesible ? to : undefined;
  const contenido = (
    <>
      <div className="indicador-titulo">{titulo}</div>
      <div className="indicador-valor">{valor}</div>
      {nota ? <div className="indicador-nota">{nota}</div> : null}
    </>
  );
  const clase = `indicador${tono ? ` indicador-${tono}` : ''}${activo ? ' activo' : ''}${className ? ` ${className}` : ''}`;
  if (enlace) {
    return (
      <Link to={enlace} className={`${clase} indicador-clicable`}>
        {contenido}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" className={`${clase} indicador-clicable`} onClick={onClick}>
        {contenido}
      </button>
    );
  }
  return <div className={clase}>{contenido}</div>;
}

export function Cargando({ texto = 'Cargando…' }: { texto?: string }): JSX.Element {
  return <div className="cargando">{texto}</div>;
}

/**
 * Pantalla de bloqueo total: reemplaza cualquier otra cosa (incluido el
 * login) cuando la versión nativa instalada quedó por debajo de la mínima, o
 * cuando el operador marcó como obligatorio el salto hasta la última versión
 * (`useSesion().actualizacionForzada`, `apps/web/src/servicios/sesion.tsx`).
 * Sin botón de "seguir" a propósito: la única salida es actualizar.
 */
export function ActualizacionRequerida({
  urlTienda,
  urlDescarga,
}: {
  urlTienda: string | null;
  urlDescarga: string | null;
}): JSX.Element {
  return (
    <div className="cargando pantalla-bloqueo">
      <strong>Hay una nueva versión disponible</strong>
      <p>Actualiza la app para seguir usándola.</p>
      {urlTienda ? (
        <a className="boton" href={urlTienda} target="_blank" rel="noreferrer">
          Actualizar ahora
        </a>
      ) : urlDescarga ? (
        <a className="boton" href={urlDescarga} target="_blank" rel="noreferrer">
          Descargar la última versión
        </a>
      ) : null}
    </div>
  );
}

/**
 * Pantalla de bloqueo total por mantenimiento del backend — independiente de
 * qué versión tenga instalada quien la ve (`useSesion().mantenimiento`).
 * `onReintentar` deja probar de nuevo sin tener que cerrar y reabrir la app.
 */
export function EnMantenimiento({
  mensaje,
  onReintentar,
  reintentando,
}: {
  mensaje: string | null;
  onReintentar: () => void;
  reintentando: boolean;
}): JSX.Element {
  return (
    <div className="cargando">
      <strong>En mantenimiento</strong>
      <p>{mensaje ?? 'Estamos realizando mejoras. Por favor, intenta más tarde.'}</p>
      <button type="button" className="boton" onClick={onReintentar} disabled={reintentando}>
        {reintentando ? 'Reintentando…' : 'Reintentar'}
      </button>
    </div>
  );
}

/**
 * Aviso NO bloqueante de que hay una versión más nueva. A diferencia de
 * `ActualizacionRequerida`, se renderiza flotando sobre la app (no reemplaza
 * el árbol de rutas) y se puede descartar (`useSesion().descartarActualizacionOpcional`).
 */
export function AvisoActualizacionOpcional({
  urlTienda,
  urlDescarga,
  onDescartar,
}: {
  urlTienda: string | null;
  urlDescarga: string | null;
  onDescartar: () => void;
}): JSX.Element {
  const url = urlTienda ?? urlDescarga;
  return (
    <div className="aviso-flotante">
      <span>Hay una nueva versión disponible.</span>
      <div className="aviso-flotante-acciones">
        {url ? (
          <a className="boton pequeno" href={url} target="_blank" rel="noreferrer">
            Actualizar
          </a>
        ) : null}
        <button type="button" className="boton secundario pequeno" onClick={onDescartar}>
          Más tarde
        </button>
      </div>
    </div>
  );
}

export function Vacio({ titulo, detalle }: { titulo: string; detalle?: string }): JSX.Element {
  return (
    <div className="vacio">
      <strong>{titulo}</strong>
      {detalle ? <span>{detalle}</span> : null}
    </div>
  );
}

export function Aviso({
  tono = 'info',
  children,
}: {
  tono?: 'info' | 'error' | 'exito' | 'atencion';
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={`aviso ${tono}`} role={tono === 'error' ? 'alert' : undefined}>
      {children}
    </div>
  );
}

/**
 * Error de un guardado/acción. Además del mensaje, lista cada campo que el
 * servidor rechazó (`detalles`: `[{campo, mensaje}]`), para que el usuario vea
 * qué falta y no solo «Los datos enviados no son válidos.».
 */
export function ErroresFormulario({ error }: { error: unknown }): JSX.Element | null {
  // Sin error (mutación aún no ejecutada o ya exitosa) no hay nada que mostrar.
  if (!error) return null;
  const e = error as ErrorApi;
  const detalles = Array.isArray(e.detalles)
    ? (e.detalles as { campo?: unknown; mensaje?: unknown }[])
    : [];
  return (
    <Aviso tono="error">
      <div>{e.message}</div>
      {detalles.length > 0 ? (
        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          {detalles.map((d, i) => (
            <li key={i}>
              {typeof d.campo === 'string' && d.campo ? `«${d.campo}»: ` : ''}
              {typeof d.mensaje === 'string' ? d.mensaje.toLowerCase() : 'dato inválido'}
            </li>
          ))}
        </ul>
      ) : null}
    </Aviso>
  );
}

/**
 * Pila de diálogos abiertos — para que el botón físico/gesto de retroceso de
 * Android cierre el modal de encima en vez de minimizar la app o saltar de
 * pantalla (§ bug reportado: con el diálogo de alta abierto en la primera
 * pantalla de la sesión, el back nativo no tenía ruta a la que volver y
 * mandaba la app a segundo plano con el formulario sin guardar).
 *
 * Un `Dialogo` se apila al montar y se desapila al desmontar; el handler del
 * back nativo (`app.tsx`) consulta esto ANTES de decidir si navega o
 * minimiza. Mismo patrón de registro module-level que los toasts globales
 * (`notificaciones-globales.tsx`), por la misma razón: quien necesita esto
 * (el listener nativo) no está dentro del árbol de React de ningún diálogo
 * en particular.
 */
const pilaDialogosAbiertos: Array<() => void> = [];

export function hayDialogoAbierto(): boolean {
  return pilaDialogosAbiertos.length > 0;
}

/** Cierra el diálogo de más arriba, si hay alguno. Devuelve si cerró algo. */
export function cerrarDialogoSuperior(): boolean {
  const cerrar = pilaDialogosAbiertos[pilaDialogosAbiertos.length - 1];
  if (!cerrar) return false;
  cerrar();
  return true;
}

/**
 * Escape cierra, y el botón atrás de Android cierra el de más arriba
 * (`pilaDialogosAbiertos`, leído por `cerrarDialogoSuperior` en `app.tsx`).
 *
 * Extraído de `Dialogo` para que otras pantallas de una sola capa —como el
 * escáner QR a pantalla completa, que no usa `Dialogo` porque necesita la
 * cámara ocupando todo el viewport, sin el cromado de panel— tengan el mismo
 * comportamiento de cierre sin duplicarlo.
 */
export function useCierreDialogo(alCerrar: () => void): void {
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') alCerrar();
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [alCerrar]);

  // Se apila una única vez por montaje (no en cada render, aunque `alCerrar`
  // cambie de identidad); la ref garantiza que esa entrada siempre invoque la
  // versión más reciente en vez de quedar con el cierre del primer render.
  const alCerrarRef = useRef(alCerrar);
  alCerrarRef.current = alCerrar;
  useEffect(() => {
    const entrada = (): void => alCerrarRef.current();
    pilaDialogosAbiertos.push(entrada);
    return () => {
      const indice = pilaDialogosAbiertos.lastIndexOf(entrada);
      if (indice !== -1) pilaDialogosAbiertos.splice(indice, 1);
    };
  }, []);
}

/**
 * Diálogo modal.
 *
 * Nació en el inventario y aquí queda compartido: toda alta y edición del
 * producto lo usa, de modo que el comportamiento de teclado y de cierre sea el
 * mismo en las nueve pantallas y no nueve variantes parecidas.
 */
export function Dialogo({
  titulo,
  ancho = 460,
  altoFijo = false,
  alCerrar,
  children,
}: {
  /** El diálogo ocupa casi toda la pantalla y es su contenido (no el diálogo) el que hace scroll. */
  altoFijo?: boolean;
  /** Texto, o un control (p. ej. un selector) cuando el título es parte de la elección. */
  titulo: ReactNode;
  /** Ancho máximo en píxeles: los formularios de ficha necesitan más que un confirmar. */
  ancho?: number;
  alCerrar: () => void;
  children: ReactNode;
}): JSX.Element {
  useCierreDialogo(alCerrar);

  return (
    <div
      className="capa-dialogo"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) alCerrar();
      }}
    >
      <div
        className="dialogo panel"
        role="dialog"
        aria-modal="true"
        aria-label={typeof titulo === 'string' ? titulo : undefined}
        style={
          altoFijo
            ? { width: `min(${ancho}px, 100%)`, height: 'min(88vh, 800px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }
            : { width: `min(${ancho}px, 100%)` }
        }
      >
        <div className="panel-cabecera">
          <h2>{titulo}</h2>
        </div>
        <div className="panel-cuerpo" style={altoFijo ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}>
          {children}
        </div>
      </div>
    </div>
  );
}

export function AccionesDialogo({
  alCerrar,
  alConfirmar,
  cargando,
  deshabilitado,
  etiqueta = 'Confirmar',
}: {
  alCerrar: () => void;
  alConfirmar: () => void;
  cargando: boolean;
  deshabilitado: boolean;
  etiqueta?: string;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
      <button type="button" className="boton secundario" onClick={alCerrar}>
        Cancelar
      </button>
      <button type="button" className="boton" onClick={alConfirmar} disabled={cargando || deshabilitado}>
        {cargando ? 'Guardando…' : etiqueta}
      </button>
    </div>
  );
}

/**
 * Confirmación de una acción destructiva.
 *
 * Se nombra el registro en el texto: «¿Eliminar?» a secas es lo que hace que la
 * gente confirme sin leer y borre la fila equivocada.
 */
export function DialogoConfirmar({
  titulo,
  mensaje,
  etiqueta = 'Eliminar',
  cargando = false,
  error,
  alCerrar,
  alConfirmar,
}: {
  titulo: string;
  mensaje: ReactNode;
  etiqueta?: string;
  cargando?: boolean;
  error?: unknown;
  alCerrar: () => void;
  alConfirmar: () => void;
}): JSX.Element {
  return (
    <Dialogo titulo={titulo} alCerrar={alCerrar}>
      {error ? <Aviso tono="error">{mensajeError(error)}</Aviso> : null}
      <p style={{ fontSize: 13.5, color: 'var(--texto-medio)', marginBottom: 14 }}>{mensaje}</p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="boton secundario" onClick={alCerrar}>
          Cancelar
        </button>
        <button type="button" className="boton peligro" onClick={alConfirmar} disabled={cargando}>
          {cargando ? 'Procesando…' : etiqueta}
        </button>
      </div>
    </Dialogo>
  );
}

/**
 * Selección de varios identificadores con casillas.
 *
 * Un `select multiple` obliga a descubrir que hay que sostener Ctrl y en el
 * móvil es directamente impracticable (§16.8); las casillas se leen y se tocan.
 */
/**
 * Umbral a partir del cual aparece el buscador: con pocas opciones (ej. tres
 * administradores) un campo de filtro es más estorbo que ayuda.
 */
const UMBRAL_BUSCADOR_SELECCION = 8;

export function SeleccionMultiple({
  etiqueta,
  opciones,
  seleccion,
  alCambiar,
  vacio = 'No hay opciones disponibles.',
  soloLectura = false,
  alCrear,
}: {
  etiqueta: string;
  opciones: { id: string; nombre: string; nota?: string }[];
  seleccion: string[];
  alCambiar: (ids: string[]) => void;
  vacio?: string;
  /** Vista de solo revisión (§ pedido): ningún control, solo lo ya marcado. */
  soloLectura?: boolean;
  /**
   * Alta al vuelo (§ pedido): si no está la opción que se busca, se crea sin
   * salir de este formulario. El llamador hace el POST y agrega el id nuevo
   * a `seleccion`; acá solo se pide el nombre y se limpia el campo al volver.
   */
  alCrear?: { etiqueta?: string; crear: (nombre: string) => Promise<void>; creando?: boolean };
}): JSX.Element {
  const [buscar, setBuscar] = useState('');
  const [nombreNuevo, setNombreNuevo] = useState('');
  const filtro = buscar.trim().toLowerCase();
  const visibles = filtro === '' ? opciones : opciones.filter((o) => o.nombre.toLowerCase().includes(filtro));

  if (soloLectura) {
    const marcadas = opciones.filter((o) => seleccion.includes(o.id));
    return (
      <div className="campo">
        <span className="campo-titulo">{etiqueta}</span>
        {marcadas.length === 0 ? (
          <p className="campo-nota">Ninguno seleccionado.</p>
        ) : (
          <div className="seleccion-multiple" role="group" aria-label={etiqueta}>
            {marcadas.map((o) => (
              <div key={o.id} className="casilla">
                <span>
                  {o.nombre}
                  {o.nota ? <em className="campo-nota"> · {o.nota}</em> : null}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="campo">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <span className="campo-titulo">{etiqueta}</span>
        {seleccion.length > 0 ? (
          <span className="campo-nota">
            {seleccion.length} de {opciones.length} seleccionado{seleccion.length === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>
      {opciones.length === 0 && !alCrear ? (
        <p className="campo-nota">{vacio}</p>
      ) : (
        <>
          {opciones.length > UMBRAL_BUSCADOR_SELECCION ? (
            <input
              type="text"
              value={buscar}
              onChange={(e) => setBuscar(e.target.value)}
              placeholder={`Filtrar ${etiqueta.toLowerCase()}…`}
              style={{ marginBottom: 6 }}
            />
          ) : null}
          {opciones.length === 0 ? <p className="campo-nota">{vacio}</p> : null}
          <div className="seleccion-multiple" role="group" aria-label={etiqueta}>
            {visibles.map((o) => (
              <label key={o.id} className="casilla">
                <input
                  type="checkbox"
                  checked={seleccion.includes(o.id)}
                  onChange={(e) =>
                    alCambiar(
                      e.target.checked ? [...seleccion, o.id] : seleccion.filter((id) => id !== o.id),
                    )
                  }
                />
                <span>
                  {o.nombre}
                  {o.nota ? <em className="campo-nota"> · {o.nota}</em> : null}
                </span>
              </label>
            ))}
            {visibles.length === 0 && opciones.length > 0 ? <p className="campo-nota">Sin resultados.</p> : null}
          </div>
        </>
      )}
      {alCrear ? (
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <input
            type="text"
            value={nombreNuevo}
            onChange={(e) => setNombreNuevo(e.target.value)}
            placeholder={alCrear.etiqueta ?? `Nuevo…`}
            style={{ flex: 1 }}
            disabled={alCrear.creando}
          />
          <button
            type="button"
            className="boton secundario pequeno"
            disabled={nombreNuevo.trim() === '' || alCrear.creando}
            onClick={async () => {
              const nombre = nombreNuevo.trim();
              if (nombre === '') return;
              await alCrear.crear(nombre);
              setNombreNuevo('');
            }}
          >
            {alCrear.creando ? 'Agregando…' : '+ Agregar'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Casilla de verificación propia, para no depender del checkbox nativo del
 * sistema operativo — su tamaño y estilo varían demasiado entre navegadores y
 * plataformas para un elemento tan visible como un ítem de checklist.
 */
export function CasillaVerificacion({
  marcada,
  alCambiar,
  etiqueta,
  deshabilitada = false,
}: {
  marcada: boolean;
  alCambiar: () => void;
  etiqueta: string;
  deshabilitada?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={marcada}
      aria-label={etiqueta}
      className={`casilla-propia${marcada ? ' marcada' : ''}`}
      disabled={deshabilitada}
      onClick={alCambiar}
    >
      {marcada ? <IconoCheck /> : null}
    </button>
  );
}

/**
 * Texto de un error para mostrar al usuario.
 *
 * El servidor ya redacta sus mensajes en español (§16.1); solo se traduce lo que
 * no viene de él, para no dejar nunca un «[object Object]» en pantalla.
 */
export function mensajeError(error: unknown, respaldo = 'No se pudo completar la operación.'): string {
  if (error instanceof ErrorApi) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return respaldo;
}

export interface AdjuntoImagen {
  id: string;
  nombreArchivo: string;
  tipoMime: string;
  tamanoBytes: number;
  entidad: EntidadAdjunto | null;
  entidadId: string | null;
  creadoEl: string;
  url: string;
  /** Clonado desde la solicitud que se aprobó: es evidencia de bitácora y no se puede quitar. */
  origenSolicitud: boolean;
}

/**
 * Fotos de una entidad (§16.3): sube al elegir el archivo, antes de que exista
 * el registro dueño, y solo entrega los ids al formulario. El vínculo con la
 * OT, la solicitud o el comentario lo hace el servidor al guardar.
 */
export function SubidaImagenes({
  etiqueta = 'Fotos',
  valor,
  alCambiar,
  maximo = 20,
  compacto = false,
  rutaAdjuntos = '/adjuntos',
}: {
  etiqueta?: string;
  valor: AdjuntoImagen[];
  alCambiar: (adjuntos: AdjuntoImagen[]) => void;
  maximo?: number;
  /** Sin etiqueta ni contador «N/máximo», solo los botones de cámara/galería
   * y las miniaturas — para empotrar en una barra angosta, como el
   * composer de comentarios. */
  compacto?: boolean;
  /** Ruta de subida. Con `/publico/…` (formulario sin sesión) no se intenta
   * borrar lo huérfano: sin sesión esa llamada devolvería 401. */
  rutaAdjuntos?: string;
}): JSX.Element {
  const [subiendo, setSubiendo] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [camaraAbierta, setCamaraAbierta] = useState(false);
  const borrarHuerfanos = !rutaAdjuntos.startsWith('/publico/') && !rutaAdjuntos.startsWith('/movil/');
  const entradaRef = useRef<HTMLInputElement>(null);

  // `valor` puede cambiar entre subidas del lote; se acumula sobre la última
  // versión con un ref para no perder fotos al subir varias seguidas.
  const valorRef = useRef(valor);
  valorRef.current = valor;

  async function subirArchivos(entrantes: File[]): Promise<void> {
    if (entrantes.length === 0) return;
    setError(null);
    const archivos = entrantes.slice(0, Math.max(0, maximo - valorRef.current.length));

    setSubiendo((n) => n + archivos.length);
    for (const archivo of archivos) {
      try {
        const formData = new FormData();
        formData.append('archivo', archivo);
        const subido = await api.subirArchivo<AdjuntoImagen>(rutaAdjuntos, formData);
        alCambiar([...valorRef.current, subido]);
      } catch (e) {
        setError(mensajeError(e, `No se pudo subir «${archivo.name}».`));
      } finally {
        setSubiendo((n) => n - 1);
      }
    }
  }

  async function quitar(adjunto: AdjuntoImagen): Promise<void> {
    // Lo clonado de una solicitud aprobada es evidencia de bitácora: ni el
    // botón se muestra para esas fotos ni se puede quitar. Lo huérfano
    // (recién subido, sin guardar) sí se borra del todo de una vez; lo ya
    // vinculado a esta entidad se desvincula de la vista y el formulario
    // dueño es quien decide, al guardar, si de verdad se elimina (así un
    // "Cancelar" no pierde evidencia ya guardada).
    if (adjunto.origenSolicitud) return;
    alCambiar(valor.filter((a) => a.id !== adjunto.id));
    if (adjunto.entidadId === null && borrarHuerfanos) {
      await api.delete(`/adjuntos/${adjunto.id}`).catch(() => undefined);
    }
  }

  return (
    <div className={compacto ? undefined : 'campo'}>
      {compacto ? null : <span className="campo-titulo">{etiqueta}</span>}

      {valor.length > 0 ? (
        <div className={compacto ? 'rejilla-imagenes rejilla-imagenes-compacta' : 'rejilla-imagenes'}>
          {valor.map((adjunto) => (
            <div key={adjunto.id} className="miniatura-imagen">
              <img src={adjunto.url} alt={adjunto.nombreArchivo} />
              {!adjunto.origenSolicitud ? (
                <button
                  type="button"
                  className="miniatura-imagen-quitar"
                  aria-label={`Quitar ${adjunto.nombreArchivo}`}
                  onClick={() => void quitar(adjunto)}
                >
                  ×
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      <input
        ref={entradaRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        multiple
        hidden
        onChange={(e) => {
          void subirArchivos(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
      <div className={compacto ? 'zona-adjuntos zona-adjuntos-compacta' : 'zona-adjuntos'}>
        {hayCamara() ? (
          <button
            type="button"
            className="boton-adjunto"
            title="Tomar foto"
            aria-label="Tomar foto"
            onClick={() => setCamaraAbierta(true)}
            disabled={valor.length >= maximo || subiendo > 0}
          >
            <IconoCamara />
          </button>
        ) : null}
        <button
          type="button"
          className="boton-adjunto"
          title="Elegir de galería"
          aria-label="Elegir de galería"
          onClick={() => entradaRef.current?.click()}
          disabled={valor.length >= maximo || subiendo > 0}
        >
          <IconoGaleria />
        </button>
        {compacto ? (
          subiendo > 0 ? <span className="zona-adjuntos-info">Subiendo… ({subiendo})</span> : null
        ) : (
          <span className="zona-adjuntos-info">
            {subiendo > 0 ? `Subiendo… (${subiendo})` : `${valor.length}/${maximo} foto(s)`}
          </span>
        )}
      </div>
      {error ? <span className="campo-error">{error}</span> : null}

      {camaraAbierta ? (
        <CapturaFoto
          alCerrar={() => setCamaraAbierta(false)}
          alCapturar={(archivo) => {
            setCamaraAbierta(false);
            void subirArchivos([archivo]);
          }}
        />
      ) : null}
    </div>
  );
}

/** «2.4 MB», «318 KB»: para que la lista de archivos diga algo, no solo el nombre. */
function formatearTamano(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ACEPTA_ARCHIVOS =
  'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,' +
  'video/mp4,video/quicktime,video/webm';

/**
 * Documentos sueltos de una entidad (§4 del backlog: «adjuntar archivos»,
 * distinto de las fotos de evidencia). Misma mecánica que `SubidaImagenes`
 * — sube al elegir, entrega solo ids — pero se listan por nombre y tamaño
 * en vez de en una grilla de miniaturas, porque no hay nada que previsualizar.
 */
export function SubidaArchivos({
  etiqueta = 'Archivos adjuntos',
  valor,
  alCambiar,
  maximo = 10,
}: {
  etiqueta?: string;
  valor: AdjuntoImagen[];
  alCambiar: (adjuntos: AdjuntoImagen[]) => void;
  maximo?: number;
}): JSX.Element {
  const [subiendo, setSubiendo] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const entradaRef = useRef<HTMLInputElement>(null);

  async function alElegirArchivos(lista: FileList | null): Promise<void> {
    if (!lista || lista.length === 0) return;
    setError(null);
    const archivos = Array.from(lista).slice(0, Math.max(0, maximo - valor.length));

    setSubiendo((n) => n + archivos.length);
    for (const archivo of archivos) {
      try {
        const formData = new FormData();
        formData.append('archivo', archivo);
        const subido = await api.subirArchivo<AdjuntoImagen>('/adjuntos', formData);
        alCambiar([...valor, subido]);
      } catch (e) {
        setError(mensajeError(e, `No se pudo subir «${archivo.name}».`));
      } finally {
        setSubiendo((n) => n - 1);
      }
    }
  }

  async function quitar(adjunto: AdjuntoImagen): Promise<void> {
    // Ver el mismo criterio en SubidaImagenes.quitar.
    if (adjunto.origenSolicitud) return;
    alCambiar(valor.filter((a) => a.id !== adjunto.id));
    if (adjunto.entidadId === null) {
      await api.delete(`/adjuntos/${adjunto.id}`).catch(() => undefined);
    }
  }

  return (
    <div className="campo">
      <span className="campo-titulo">{etiqueta}</span>

      {valor.length > 0 ? (
        <ListaArchivos
          archivos={valor}
          alQuitar={(a) => void quitar(a)}
        />
      ) : null}

      <input
        ref={entradaRef}
        type="file"
        accept={ACEPTA_ARCHIVOS}
        multiple
        hidden
        onChange={(e) => {
          void alElegirArchivos(e.target.files);
          e.target.value = '';
        }}
      />
      <div className="zona-adjuntos">
        <button
          type="button"
          className="boton-adjunto"
          title="Agregar archivo o video"
          aria-label={subiendo > 0 ? `Subiendo… (${subiendo})` : 'Agregar archivo o video'}
          onClick={() => entradaRef.current?.click()}
          disabled={valor.length >= maximo || subiendo > 0}
        >
          <IconoArchivo />
        </button>
        <span className="zona-adjuntos-info">
          {subiendo > 0 ? `Subiendo… (${subiendo})` : `${valor.length}/${maximo} archivo(s)`}
        </span>
      </div>
      {error ? <span className="campo-error">{error}</span> : null}
    </div>
  );
}

/** Lista de solo lectura para mostrar documentos ya guardados (detalle de una OT). */
export function ListaArchivos({
  archivos,
  alQuitar,
}: {
  archivos: AdjuntoImagen[];
  alQuitar?: (adjunto: AdjuntoImagen) => void;
}): JSX.Element {
  return (
    <div className="lista-archivos">
      {archivos.map((archivo) => (
        <div key={archivo.id} className="fila-archivo">
          <span className="fila-archivo-icono" aria-hidden="true">
            {archivo.tipoMime.startsWith('video/') ? '🎥' : '📄'}
          </span>
          <a href={archivo.url} target="_blank" rel="noreferrer" className="fila-archivo-nombre">
            {archivo.nombreArchivo}
          </a>
          <span className="fila-archivo-tamano">{formatearTamano(archivo.tamanoBytes)}</span>
          {alQuitar && !archivo.origenSolicitud ? (
            <button
              type="button"
              className="fila-archivo-quitar"
              aria-label={`Quitar ${archivo.nombreArchivo}`}
              onClick={() => alQuitar(archivo)}
            >
              ×
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * Fotos y archivos en una sola sección (§16.3): comparten una única fila de
 * botones — cámara, galería, archivo — en vez de repetir la zona de adjuntos
 * dos veces cuando un formulario admite ambos tipos.
 */
export function SubidaFotosYArchivos({
  etiqueta = 'Fotos y archivos',
  fotos,
  archivos,
  alCambiarFotos,
  alCambiarArchivos,
  maximoFotos = 20,
  maximoArchivos = 10,
}: {
  etiqueta?: string;
  fotos: AdjuntoImagen[];
  archivos: AdjuntoImagen[];
  alCambiarFotos: (adjuntos: AdjuntoImagen[]) => void;
  alCambiarArchivos: (adjuntos: AdjuntoImagen[]) => void;
  maximoFotos?: number;
  maximoArchivos?: number;
}): JSX.Element {
  const [subiendo, setSubiendo] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [camaraAbierta, setCamaraAbierta] = useState(false);
  const entradaImagenRef = useRef<HTMLInputElement>(null);
  const entradaArchivoRef = useRef<HTMLInputElement>(null);

  // Igual que en `SubidaImagenes`: subidas en lote pueden solaparse, así que
  // se acumula sobre la última versión con un ref y no sobre `valor` cerrado
  // en el momento en que empezó la subida.
  const fotosRef = useRef(fotos);
  fotosRef.current = fotos;
  const archivosRef = useRef(archivos);
  archivosRef.current = archivos;

  async function subir(entrantes: File[], destino: 'foto' | 'archivo'): Promise<void> {
    if (entrantes.length === 0) return;
    setError(null);
    const ref = destino === 'foto' ? fotosRef : archivosRef;
    const maximo = destino === 'foto' ? maximoFotos : maximoArchivos;
    const alCambiar = destino === 'foto' ? alCambiarFotos : alCambiarArchivos;
    const lote = entrantes.slice(0, Math.max(0, maximo - ref.current.length));

    setSubiendo((n) => n + lote.length);
    for (const archivo of lote) {
      try {
        const formData = new FormData();
        formData.append('archivo', archivo);
        const subido = await api.subirArchivo<AdjuntoImagen>('/adjuntos', formData);
        alCambiar([...ref.current, subido]);
      } catch (e) {
        setError(mensajeError(e, `No se pudo subir «${archivo.name}».`));
      } finally {
        setSubiendo((n) => n - 1);
      }
    }
  }

  async function quitarFoto(adjunto: AdjuntoImagen): Promise<void> {
    alCambiarFotos(fotos.filter((a) => a.id !== adjunto.id));
    if (adjunto.entidadId === null) await api.delete(`/adjuntos/${adjunto.id}`).catch(() => undefined);
  }

  async function quitarArchivo(adjunto: AdjuntoImagen): Promise<void> {
    alCambiarArchivos(archivos.filter((a) => a.id !== adjunto.id));
    if (adjunto.entidadId === null) await api.delete(`/adjuntos/${adjunto.id}`).catch(() => undefined);
  }

  return (
    <div className="campo" style={{ marginBottom: 0 }}>
      {etiqueta ? <span className="campo-titulo">{etiqueta}</span> : null}

      {fotos.length > 0 ? (
        <div className="rejilla-imagenes">
          {fotos.map((adjunto) => (
            <div key={adjunto.id} className="miniatura-imagen">
              <img src={adjunto.url} alt={adjunto.nombreArchivo} />
              <button
                type="button"
                className="miniatura-imagen-quitar"
                aria-label={`Quitar ${adjunto.nombreArchivo}`}
                onClick={() => void quitarFoto(adjunto)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {archivos.length > 0 ? <ListaArchivos archivos={archivos} alQuitar={(a) => void quitarArchivo(a)} /> : null}

      <input
        ref={entradaImagenRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        multiple
        hidden
        onChange={(e) => {
          void subir(Array.from(e.target.files ?? []), 'foto');
          e.target.value = '';
        }}
      />
      <input
        ref={entradaArchivoRef}
        type="file"
        accept={ACEPTA_ARCHIVOS}
        multiple
        hidden
        onChange={(e) => {
          void subir(Array.from(e.target.files ?? []), 'archivo');
          e.target.value = '';
        }}
      />

      <div className="zona-adjuntos">
        {hayCamara() ? (
          <button
            type="button"
            className="boton-adjunto"
            title="Tomar foto"
            aria-label="Tomar foto"
            onClick={() => setCamaraAbierta(true)}
            disabled={fotos.length >= maximoFotos || subiendo > 0}
          >
            <IconoCamara />
          </button>
        ) : null}
        <button
          type="button"
          className="boton-adjunto"
          title="Elegir foto de galería"
          aria-label="Elegir foto de galería"
          onClick={() => entradaImagenRef.current?.click()}
          disabled={fotos.length >= maximoFotos || subiendo > 0}
        >
          <IconoGaleria />
        </button>
        <button
          type="button"
          className="boton-adjunto"
          title="Agregar archivo o video"
          aria-label="Agregar archivo o video"
          onClick={() => entradaArchivoRef.current?.click()}
          disabled={archivos.length >= maximoArchivos || subiendo > 0}
        >
          <IconoArchivo />
        </button>
        <span className="zona-adjuntos-info">
          {subiendo > 0
            ? `Subiendo… (${subiendo})`
            : `${fotos.length}/${maximoFotos} foto(s) · ${archivos.length}/${maximoArchivos} archivo(s)`}
        </span>
      </div>
      {error ? <span className="campo-error">{error}</span> : null}

      {camaraAbierta ? (
        <CapturaFoto
          alCerrar={() => setCamaraAbierta(false)}
          alCapturar={(archivo) => {
            setCamaraAbierta(false);
            void subir([archivo], 'foto');
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Galería de solo lectura con visor en modal (regla general del proyecto:
 * cualquier imagen debe poder abrirse en grande al hacer click, no solo
 * enlazar a `target="_blank"`). Se usa en cualquier detalle que muestre
 * fotos ya guardadas — OT, activo, ubicación, solicitud, comentario.
 */
export function GaleriaImagenes({
  fotos,
  titulo,
}: {
  fotos: AdjuntoImagen[];
  /** Título del diálogo del visor; por omisión usa el nombre del archivo. */
  titulo?: (foto: AdjuntoImagen) => string;
}): JSX.Element | null {
  const [abierta, setAbierta] = useState<AdjuntoImagen | null>(null);

  if (fotos.length === 0) return null;

  return (
    <>
      <div className="rejilla-imagenes">
        {fotos.map((foto) => (
          <button
            key={foto.id}
            type="button"
            className="miniatura-imagen miniatura-imagen-boton"
            onClick={() => setAbierta(foto)}
            aria-label={`Ver ${foto.nombreArchivo}`}
          >
            <img src={foto.url} alt={foto.nombreArchivo} />
          </button>
        ))}
      </div>

      {abierta ? (
        <Dialogo titulo={titulo ? titulo(abierta) : abierta.nombreArchivo} ancho={720} alCerrar={() => setAbierta(null)}>
          <img
            src={abierta.url}
            alt={abierta.nombreArchivo}
            style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block' }}
          />
        </Dialogo>
      ) : null}
    </>
  );
}

/**
 * Cuadro vacío en el mismo tamaño que ocuparía la foto — para cuando no hay
 * imagen, en vez de dejar el espacio en blanco o saltar el tamaño de la
 * columna/tarjeta. Base reutilizable: cualquier miniatura de foto de la app
 * (tabla, tarjeta, ficha) debería usar este mismo cuadro como su estado sin
 * imagen, no inventar uno propio.
 */
export function PlaceholderImagen({ tamano = 36, radio = 6 }: { tamano?: number; radio?: number }): JSX.Element {
  return (
    <div
      aria-hidden="true"
      style={{
        width: tamano,
        height: tamano,
        borderRadius: radio,
        background: 'var(--azul-tenue)',
        border: '1px solid var(--azul-suave, var(--azul-tenue))',
        display: 'grid',
        placeItems: 'center',
        color: 'var(--azul)',
        flex: 'none',
      }}
    >
      <IconoGaleria />
    </div>
  );
}

/**
 * Miniatura de una sola foto para una fila de tabla (regla general: toda
 * tabla de una entidad con imágenes debe mostrar la miniatura correspondiente,
 * y toda imagen debe poder abrirse en grande al hacer click). Si no hay foto,
 * muestra el cuadro vacío de `PlaceholderImagen` en vez de nada, para que la
 * columna no salte de tamaño.
 */
export function MiniaturaImagenTabla({
  url,
  nombre,
  tamano = 36,
}: {
  url: string | null;
  nombre: string;
  tamano?: number;
}): JSX.Element {
  const [abierta, setAbierta] = useState(false);

  if (!url) {
    return <PlaceholderImagen tamano={tamano} />;
  }

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          // Varias filas clicables (tarjetas móviles de Solicitudes, por
          // ejemplo) navegan al detalle con un `onClick` en el contenedor; sin
          // frenar la burbuja acá, tocar la foto abría el modal Y navegaba al
          // mismo tiempo (§ bug reportado).
          e.stopPropagation();
          setAbierta(true);
        }}
        aria-label={`Ver foto de ${nombre}`}
        style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', display: 'block' }}
      >
        <img
          src={url}
          alt={nombre}
          style={{ width: tamano, height: tamano, borderRadius: 6, objectFit: 'cover', display: 'block' }}
        />
      </button>

      {abierta ? (
        <Dialogo titulo={nombre} ancho={720} alCerrar={() => setAbierta(false)}>
          <img src={url} alt={nombre} style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block' }} />
        </Dialogo>
      ) : null}
    </>
  );
}

/**
 * Imagen de un código QR ya asignado (§16.2/§17): el backend solo guarda el
 * código alfanumérico corto (13 caracteres) — la imagen se genera en el
 * cliente a partir de él, no hace falta persistirla en ningún lado.
 */
export function CodigoQr({
  codigo,
  tamano = 160,
  enlacePublico = false,
}: {
  codigo: string;
  tamano?: number;
  /**
   * Codifica el enlace de solicitud pública (`/s/<código>`) en vez del código a
   * secas. Es el que se imprime para que la cámara del celular lo abra.
   */
  enlacePublico?: boolean;
}): JSX.Element | null {
  const [url, setUrl] = useState<string | null>(null);
  const { usuario } = useSesion();
  const contenido = enlacePublico ? enlaceSolicitudPublica(codigo, usuario?.tenantSlug) : codigo;

  useEffect(() => {
    let vigente = true;
    QRCode.toDataURL(contenido, { width: tamano, margin: 1 })
      .then((generado) => {
        if (vigente) setUrl(generado);
      })
      .catch(() => undefined);
    return () => {
      vigente = false;
    };
  }, [contenido, tamano]);

  if (!url) return null;
  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <img src={url} width={tamano} height={tamano} alt={`Código QR ${codigo}`} style={{ borderRadius: 8 }} />
      <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: 'var(--texto-tenue)', wordBreak: 'break-all', textAlign: 'center' }}>
        {enlacePublico ? contenido : codigo}
      </span>
    </div>
  );
}

/**
 * Enlace público de una sede. Lleva la organización (`?t=`) para que el servidor no tenga
 * que adivinarla: un código repetido entre organizaciones se resuelve así sin ambigüedad.
 */
export function enlaceSolicitudPublica(codigo: string, tenantSlug?: string): string {
  const t = tenantSlug ? `?t=${encodeURIComponent(tenantSlug)}` : '';
  return `${window.location.origin}/s/${codigo}${t}`;
}

/**
 * QR público de una sede padre, para copiarlo o descargarlo. Se genera al abrir
 * el diálogo y no se guarda: no cambia nada del registro ni de la base.
 */
export function DialogoQrPublico({
  codigo,
  nombre,
  alCerrar,
}: {
  codigo: string;
  /** Nombre de la sede: va en el nombre del archivo que se descarga. */
  nombre: string;
  alCerrar: () => void;
}): JSX.Element {
  // El canvas solo dibuja (QR + logo) a 840 px; lo que se muestra es una imagen de 280 px.
  const lienzo = useRef<HTMLCanvasElement>(null);
  const [imagen, setImagen] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const { usuario } = useSesion();
  const enlace = enlaceSolicitudPublica(codigo, usuario?.tenantSlug);

  useEffect(() => {
    const canvas = lienzo.current;
    if (!canvas) return undefined;
    let vigente = true;
    // Nivel H de corrección: el logo tapa parte del código y aun así se lee.
    // 840 px internos: el logo y los módulos se ven nítidos, y la copia o descarga también.
    void QRCode.toCanvas(canvas, enlace, {
      width: 840,
      margin: 2,
      errorCorrectionLevel: 'H',
      color: { dark: '#000000', light: '#ffffff' },
    }).then(() => {
      if (!vigente) return;
      setImagen(canvas.toDataURL('image/png'));
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const logo = new Image();
      logo.onload = () => {
        if (!vigente) return;
        const lado = canvas.width * 0.24;
        const x = (canvas.width - lado) / 2;
        const y = (canvas.height - lado) / 2;
        // Fondo blanco redondeado detrás del logo, para que los módulos no lo ensucien.
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.roundRect(x - 8, y - 8, lado + 16, lado + 16, 14);
        ctx.fill();
        // El logo se pasa a blanco y negro (lo oscuro queda negro, lo claro blanco) para
        // que combine con el QR. La transparencia se conserva.
        const temporal = document.createElement('canvas');
        temporal.width = logo.naturalWidth;
        temporal.height = logo.naturalHeight;
        const tctx = temporal.getContext('2d');
        if (!tctx) return;
        tctx.drawImage(logo, 0, 0);
        const datos = tctx.getImageData(0, 0, temporal.width, temporal.height);
        const px = datos.data;
        for (let i = 0; i < px.length; i += 4) {
          const luminancia = 0.299 * px[i]! + 0.587 * px[i + 1]! + 0.114 * px[i + 2]!;
          const valor = luminancia < 128 ? 0 : 255;
          px[i] = valor;
          px[i + 1] = valor;
          px[i + 2] = valor;
        }
        tctx.putImageData(datos, 0, 0);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(temporal, x, y, lado, lado);
        setImagen(canvas.toDataURL('image/png'));
      };
      logo.src = '/logo-continua-qr.png';
    });
    return () => {
      vigente = false;
    };
  }, [enlace]);

  async function copiar(): Promise<void> {
    setAviso(null);
    try {
      const blob = await new Promise<Blob | null>((resolver) => lienzo.current?.toBlob(resolver, 'image/png') ?? resolver(null));
      if (!blob) throw new Error('Sin imagen');
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setAviso('QR copiado.');
    } catch {
      setAviso('Este navegador no permite copiar imágenes. Use «Descargar».');
    }
  }

  function descargar(): void {
    const url = lienzo.current?.toDataURL('image/png');
    if (!url) return;
    const ancla = document.createElement('a');
    ancla.href = url;
    // Caracteres que Windows no admite en un nombre de archivo se reemplazan.
    const nombreArchivo = nombre.replace(/[\\/:*?"<>|]/g, '').trim();
    ancla.download = `QR ${nombreArchivo}.png`;
    ancla.click();
  }

  return (
    <Dialogo titulo="QR público de la sede" alCerrar={alCerrar} ancho={380}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <canvas ref={lienzo} aria-hidden="true" style={{ display: 'none' }} />
        {imagen ? (
          <img src={imagen} width={280} height={280} alt={`QR público ${codigo}`} style={{ borderRadius: 8 }} />
        ) : null}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="boton" onClick={() => void copiar()}>
            Copiar QR
          </button>
          <button type="button" className="boton secundario" onClick={descargar}>
            Descargar
          </button>
        </div>
        {aviso ? <span className="campo-nota">{aviso}</span> : null}
      </div>
    </Dialogo>
  );
}

/**
 * Números de página a mostrar: primera, última, la actual y sus vecinas
 * inmediatas — el resto se colapsa en un «…» (`null`) para que 52 páginas no
 * se conviertan en 52 botones. Sin esto, saltar de la página 1 a la 40 exige
 * apretar «Siguiente» 39 veces (§ pedido: la paginación anterior solo tenía
 * Anterior/Siguiente, nada intuitivo con listas largas como Activos).
 */
function numerosDePagina(pagina: number, paginas: number): (number | null)[] {
  const vecinos = new Set([1, paginas, pagina - 1, pagina, pagina + 1]);
  const numeros = [...vecinos].filter((n) => n >= 1 && n <= paginas).sort((a, b) => a - b);

  const resultado: (number | null)[] = [];
  let anterior = 0;
  for (const n of numeros) {
    if (anterior && n - anterior > 1) resultado.push(null);
    resultado.push(n);
    anterior = n;
  }
  return resultado;
}

export function Paginacion({
  pagina,
  tamano,
  total,
  onCambiar,
}: {
  pagina: number;
  tamano: number;
  total: number;
  onCambiar: (pagina: number) => void;
}): JSX.Element | null {
  const esMovil = useEsMovil();
  const paginas = Math.ceil(total / tamano);
  // Se muestra también con una sola página (rango «1–N de N»), para que la
  // tabla siempre diga cuántos registros hay.
  if (total === 0) return null;

  const desde = (pagina - 1) * tamano + 1;
  const hasta = Math.min(pagina * tamano, total);

  return (
    <div className="paginacion">
      <span className="paginacion-rango">
        {desde}–{hasta} de {total}
      </span>
      <div className="paginacion-controles">
        <button
          type="button"
          className="boton-icono"
          title="Primera página"
          aria-label="Primera página"
          disabled={pagina <= 1}
          onClick={() => onCambiar(1)}
        >
          «
        </button>
        <button
          type="button"
          className="boton secundario pequeno"
          disabled={pagina <= 1}
          onClick={() => onCambiar(pagina - 1)}
        >
          Anterior
        </button>

        {esMovil ? (
          <span className="paginacion-actual">
            Página {pagina} de {paginas}
          </span>
        ) : (
          <div className="paginacion-numeros">
            {numerosDePagina(pagina, paginas).map((n, i) =>
              n === null ? (
                <span key={`ellipsis-${i}`} className="paginacion-elipsis">
                  …
                </span>
              ) : (
                <button
                  key={n}
                  type="button"
                  className={`paginacion-numero${n === pagina ? ' activo' : ''}`}
                  aria-current={n === pagina ? 'page' : undefined}
                  onClick={() => onCambiar(n)}
                >
                  {n}
                </button>
              ),
            )}
          </div>
        )}

        <button
          type="button"
          className="boton secundario pequeno"
          disabled={pagina >= paginas}
          onClick={() => onCambiar(pagina + 1)}
        >
          Siguiente
        </button>
        <button
          type="button"
          className="boton-icono"
          title="Última página"
          aria-label="Última página"
          disabled={pagina >= paginas}
          onClick={() => onCambiar(paginas)}
        >
          »
        </button>
      </div>
    </div>
  );
}
